import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { HiOutlineMailOpen } from 'react-icons/hi';
import ActionBar from '../../../components/ui/ActionBar';
import Loader from '../../../components/ui/Loader';
import DataGrid from '../../../components/DataGrid';
import OverflowText from '../../../components/ui/OverflowTooltip';
import GridButton from '../../../components/ui/GridButton';
import Icon from '../../../components/ui/Icon';
import {
    getNotificationSummaryByTarget,
    markAllNotificationsAsRead,
    markNotificationAsRead,
} from '../../../ducks/notifications';
import { dateObjectEquals, toDateTime } from '../../../utils/utils';
import {
    AUDIENCE_APPLICATION_STATUS,
    NOIFICATION_APPLICATION,
    NOTIFICATION_SOURCE,
    NOTIFICATION_STATUS_VALUE,
} from '../../../constants';
import withAuth from '../../../components/hoc/withAuth';
import { AppLoadContext } from '../../../components/ui/AppLoadContext';
import styles from './Notifications.module.scss';

export const Cell = ({ status, children }) => {
    const unread = status !== NOTIFICATION_STATUS_VALUE.Read;
    const cellStyle = unread ? 'fw-bold' : '';

    return (
        <div className={cellStyle}>{children}</div>
    )
}

export const Notifications = () => {
    const dispatch = useDispatch();
    const isAppLoaded = useContext(AppLoadContext);
    const location = useLocation();

    const currentUser = useSelector((state) => state.user.currentUser);
    const { pageNotifications, unReadCount } = useSelector((state) => state.notifications);

    const [tableFilter, setTableFilter] = useState([]);
    const [state] = useState(location.state || {});

    const defaultSort = [
        {
            id: 'createdOn',
            desc: true,
        },
    ];

    const getMessageSummary = (memberList, agShortName) => {
        let namesRetiredMembers = [];
        let namesPanelMembers = [];

        if (memberList.retiredMembers && memberList.retiredMembers?.length > 0) {
            for (let item of memberList.retiredMembers) {
                namesRetiredMembers.push(`${item.firstname} ${item.lastname}`);
            }
        }
        if (memberList.panelMembers && memberList.panelMembers?.length > 0) {
            for (let item of memberList.panelMembers) {
                namesPanelMembers.push(`${item.firstname} ${item.lastname}`);
            }
        }

        let retiredText = '';
        if (namesRetiredMembers.length > 0) {
            retiredText = 'Applicants Retired ' + namesRetiredMembers.join(', ');
        }

        let panelText = '';
        if (namesPanelMembers.length > 0) {
            panelText = 'Review and update Member Panel for ' + namesPanelMembers.join(', ')
        }
        return (
            (namesPanelMembers.length + namesRetiredMembers.length) +
            ' members were unenrolled from ' + agShortName + ' health plan.\n' + panelText + '. ' + retiredText + '.'
        );
    }

    const columns = [
        {
            id: 'createdOn',
            header: 'Date/Time',
            accessorKey: 'createdOn',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.createdOn)),
            cell: ({ row }) => <Cell status={row.original.status}>{toDateTime(row.original.createdOn)}</Cell>,
            sortingFn: 'datetime',
            filterFn: dateObjectEquals,
            size: 160,
        },
        {
            id: 'target',
            header: 'Application',
            sortType: 'text',
            accessorKey: 'target',
            cell: ({ row }) => <Cell status={row.original.status}>{NOIFICATION_APPLICATION[row.original.target]}</Cell>,
            accessorFn: (row) => `${NOIFICATION_APPLICATION[row.target]}`,
            size: 200,
        },
        {
            id: 'source',
            header: 'Source',
            sortType: 'text',
            cell: ({ row }) => <Cell status={row.original.status}>{NOTIFICATION_SOURCE[row.original.source]}</Cell>,
            accessorFn: (row) => `${NOTIFICATION_SOURCE[row.source]}`,
            size: 200,
        },
        {
            id: 'agShortName',
            header: 'AG Short Name',
            accessorKey: 'agShortName',
            cell: ({ row }) => <Cell status={row.original.status}>{row.original.agShortName}</Cell>,
            size: 150,
            filterFn: 'equals',
        },
        {
            id: 'message',
            header: 'Message',
            accessorKey: 'message',
            cell: ({ row, cell }) => <OverflowText id={cell.id} tooltipStyle={styles.tooltip}>{getMessageSummary(row.original.memberList, row.original.agShortName)}</OverflowText>,
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <>
                    {row.original.status !== NOTIFICATION_STATUS_VALUE.Read ? (
                        <GridButton
                            variant="light" size="sm" className="py-0 lh-1" title="Mark as Read"
                            onClick={() => handleMarkRead(row.original.id)}>
                            <Icon icon="view" size={14} />
                        </GridButton>)
                        :
                        (<Icon icon="check" size={14} className="text-success mx-2" />)
                    }
                </>
            ),
            size: 60,
        }
    ];

    const handleMarkRead = (id) => {
        dispatch(markNotificationAsRead(id));
    }

    const handleMarkAllRead = () => {
        const notificationIds = pageNotifications
            ?.filter(notification => notification.status !== NOTIFICATION_STATUS_VALUE.Read)
            .map(notification => notification.id);
        if (notificationIds?.length > 0) {
            dispatch(markAllNotificationsAsRead(notificationIds));
        }
    }

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            if (!pageNotifications) {
                dispatch(getNotificationSummaryByTarget(currentUser?.companyId, AUDIENCE_APPLICATION_STATUS.ADVISORY_GROUP))
            }
        }
    }, [isAppLoaded, pageNotifications]);

    useEffect(() => {
        if (state && state.agId) {
            const ag = pageNotifications?.find(item => item.agId === parseInt(state.agId));
            if (ag) {
                setTableFilter(() => [{ id: 'agShortName', value: ag.agShortName }]);
            } else {
                setTableFilter(() => [{ id: 'agShortName', value: undefined }])
            }
        } else {
            setTableFilter([]);
        }
    }, [location.pathname, pageNotifications]);

    return (
        <>
            <ActionBar title="Notifications/Alerts">
                <Button type="button" disabled={unReadCount === 0} onClick={handleMarkAllRead}>
                    <HiOutlineMailOpen className="me-1" />Mark all as Read
                </Button>
            </ActionBar>
            {(pageNotifications === null) ? (
                <Loader />
            ) : (
                <DataGrid
                    columns={columns}
                    data={pageNotifications}
                    sort={defaultSort}
                    filter={tableFilter}
                />
            )}
        </>
    )
}

export default withAuth(Notifications);
